import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "../api/axios";
import { useLanguage } from "../context/LanguageContext";
// --- SVG ICONS ---
const Icons = {
    VerifiedBadge: () => (<svg className="w-20 h-20 text-emerald-500 drop-shadow-xl" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" fill="#ECFDF5" stroke="#10B981" strokeWidth="1.5"/>
      <path d="M7 12L10 15L17 8" stroke="#059669" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>),
    Calendar: () => (<svg className="w-5 h-5 text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
    </svg>),
    Layer: () => (<svg className="w-5 h-5 text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"/>
    </svg>),
    BookOpen: () => (<svg className="w-5 h-5 text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"/>
    </svg>),
};
const VerifyCertif = () => {
    const { token } = useParams();
    const { t, lang: language } = useLanguage();
    const tr = (key, fallback) => t?.ui?.verifyCertif?.[key] || t?.[key] || fallback;
    const [certif, setCertif] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const formatDate = (dateString) => {
        if (!dateString)
            return "—";
        const locale = language === "fr" ? (document.documentElement.lang === "fr" ? "fr-FR" : "en-GB") : "en-US";
        return new Date(dateString).toLocaleDateString(locale, {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
    };
    useEffect(() => {
        const fetchCertif = async () => {
            try {
                const certificateResponse = await api.get(
                    `/api/certifs/verify/${token}`
                );
                setCertif(certificateResponse.data);
            }
            catch (err) {
                setError(tr("invalidLink", "Lien invalide ou expiré."));
            }
            finally {
                setLoading(false);
            }
        };
        fetchCertif();
    }, [token, t]);
    // Les compétences proviennent de l'instantané enregistré dans le certificat.
    // Une modification future du catalogue d'une entreprise ne change donc
    // jamais un certificat déjà émis.
    const getCompetences = () => {
        if (!certif)
            return [];
        const languageKey = language === "fr" ? "fr" : "en";
        const snapshotCompetences = certif?.competenciesI18n?.[languageKey];
        if (Array.isArray(snapshotCompetences) && snapshotCompetences.length > 0)
            return snapshotCompetences;
        const themeSkills = t?.skills?.[certif.theme];
        const translatedSkills = themeSkills?.[certif.subTheme];
        return Array.isArray(translatedSkills) ? translatedSkills : [];
    };
    const competences = getCompetences();
    if (loading)
        return (<div className="min-h-screen bg-gradient-to-br from-slate-50 to-white flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <div className="w-12 h-12 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin"></div>
          <span className="text-indigo-900 font-semibold tracking-wide text-sm animate-pulse uppercase">
            {t.verifying}...
          </span>
        </div>
      </div>);
    if (error)
        return (<div className="min-h-screen bg-gradient-to-br from-slate-50 to-white flex items-center justify-center p-6">
        <div className="bg-white p-8 rounded-3xl shadow-2xl border border-red-100 max-w-sm w-full text-center">
          <div className="text-red-500 text-5xl mb-4">✕</div>
          <h2 className="text-xl font-bold text-gray-800 mb-2">{t.notVerified}</h2>
          <p className="text-gray-500 mb-6 text-sm">{error}</p>
          <a href="/" className="text-indigo-600 hover:text-indigo-800 font-medium text-sm transition">
            {t.backHome} &rarr;
          </a>
        </div>
      </div>);
    return (<div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center p-4 sm:p-6 relative overflow-hidden">
      
      {/* --- DECORATIVE BACKGROUND --- */}
      <div className="absolute top-[-12%] left-[-12%] w-[45%] h-[45%] bg-indigo-200/25 rounded-full blur-[110px] pointer-events-none"></div>
      <div className="absolute bottom-[-12%] right-[-12%] w-[45%] h-[45%] bg-emerald-200/25 rounded-full blur-[110px] pointer-events-none"></div>
      <div className="absolute top-0 left-1/2 w-[80%] h-[1px] bg-gradient-to-r from-transparent via-indigo-200 to-transparent opacity-30"/>

      {/* --- CERTIFICATE CARD --- */}
      <div className="bg-white w-full max-w-5xl rounded-[2.5rem] shadow-2xl shadow-indigo-100/50 border border-white/60 relative overflow-hidden flex flex-col md:flex-row animate-fade-in-up">
        
        {/* LEFT COLUMN: VISUAL & STATUS */}
        <div className="bg-gradient-to-br from-indigo-50 to-white md:w-1/3 p-10 flex flex-col items-center justify-center text-center border-b md:border-b-0 md:border-r border-indigo-50">
          <div className="mb-6 transform hover:scale-105 transition duration-500">
            <Icons.VerifiedBadge />
          </div>
          <h2 className="text-emerald-700 font-bold text-lg tracking-wider uppercase mb-1">{t.authentic}</h2>
          <p className="text-xs text-gray-400 font-medium">{t.verifiedBy}</p>
          
          <div className="mt-10 w-full border-t border-indigo-100 pt-7">
             <p className="text-xs text-indigo-400 uppercase tracking-widest font-bold mb-2">{t.certificateId}</p>
             <p className="font-mono text-xs text-gray-600 bg-white border border-indigo-50 py-3 px-4 rounded-xl shadow-sm break-all">
               {certif.identifier}
             </p>
          </div>
        </div>

        {/* RIGHT COLUMN: DETAILS */}
        <div className="flex-1 p-10 md:p-14">
          
          {/* Name Header */}
          <div className="mb-12">
            <p className="text-sm font-semibold text-gray-400 uppercase tracking-widest mb-3">{t.certifiesThat}</p>
            <h1 className="text-4xl md:text-5xl font-serif text-gray-900 font-bold leading-tight bg-clip-text text-transparent bg-gradient-to-r from-gray-900 via-indigo-900 to-gray-800">
              {certif.participant?.name}
            </h1>
            <div className="h-1 w-28 bg-gradient-to-r from-indigo-500 to-emerald-400 mt-6 rounded-full"></div>
          </div>

          {/* Info Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-8 gap-x-12">
            
            <DetailGroup label={t.mainTheme} value={certif.theme} isPrimary/>
            
            <DetailGroup label={t.specialization} value={certif.subTheme} icon={<Icons.Layer />}/>

            <div className="col-span-1 sm:col-span-2 grid grid-cols-2 gap-4 bg-gray-50 p-6 rounded-2xl border border-gray-100 shadow-sm">
               <div>
                  <div className="flex items-center space-x-2 mb-1">
                    <Icons.Calendar />
                    <span className="text-xs font-bold text-gray-400 uppercase">{t.startDate}</span>
                  </div>
                  <p className="text-sm font-semibold text-gray-700">{formatDate(certif.start_date)}</p>
               </div>
               <div className="border-l border-gray-200 pl-5">
                  <div className="flex items-center space-x-2 mb-1">
                    <Icons.Calendar />
                    <span className="text-xs font-bold text-gray-400 uppercase">{t.endDate}</span>
                  </div>
                  <p className="text-sm font-semibold text-gray-700">{formatDate(certif.end_date)}</p>
               </div>
            </div>

            <DetailGroup label={t.duration} value={`${certif.duration} ${t.days}`}/>

            {/* --- COMPETENCES REQUISES SECTION --- */}
            {competences && competences.length > 0 && (<div className="col-span-1 sm:col-span-2 pt-4 border-t border-dashed border-gray-200">
                  <div className="flex items-center space-x-2 mb-3">
                    <Icons.BookOpen />
                    <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">{t.requiredSkills}</span>
                  </div>
                  
                  <ul className="grid gap-2">
                    {competences.map((comp, index) => (<li key={index} className="flex items-start text-sm text-gray-700 font-medium">
                        <span className="inline-block w-1.5 h-1.5 rounded-full bg-indigo-400 mt-1.5 mr-2 flex-shrink-0"></span>
                        {comp}
                      </li>))}
                  </ul>
              </div>)}

          </div>

          {/* Discreet Footer */}
          <div className="mt-12 pt-7 border-t border-gray-100 flex flex-col sm:flex-row justify-between items-center text-xs text-gray-400">
             <p>{t.officialDocument}</p>
             <p className="mt-2 sm:mt-0 flex items-center">
               <span className="w-2 h-2 bg-emerald-500 rounded-full mr-2 animate-pulse"></span>
               {t.validCertificate}
             </p>
          </div>

        </div>
      </div>
      
      <style jsx>{`
        @keyframes fade-in-up {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in-up {
          animation: fade-in-up 0.8s ease-out forwards;
        }
      `}</style>
    </div>);
};
// Helper Component
const DetailGroup = ({ label, value, icon, isPrimary = false }) => (<div className="flex flex-col">
    <div className="flex items-center space-x-2 mb-2">
      {icon && icon}
      <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">{label}</span>
    </div>
    <p className={`${isPrimary ? 'text-xl text-indigo-900 font-bold' : 'text-lg text-gray-700 font-medium'}`}>
      {value}
    </p>
  </div>);
export default VerifyCertif;
